import { useCallback, useEffect, useRef, useState } from "react";
import { IconCamera, IconRefresh } from "./icons";

export interface CapturedSelfie {
  dataUrl: string;
  base64: string;
  mimeType: string;
}

interface CameraCaptureProps {
  disabled?: boolean;
  onCapture: (selfie: CapturedSelfie) => void;
  capturedDataUrl: string | null;
  onClear: () => void;
}

function dataUrlToParts(dataUrl: string): { base64: string; mimeType: string } {
  const match = /^data:(image\/[a-zA-Z0-9.+-]+);base64,(.+)$/.exec(dataUrl);
  if (!match || !match[1] || !match[2]) {
    throw new Error("Invalid image data URL");
  }
  return { mimeType: match[1], base64: match[2] };
}

export function CameraCapture({
  disabled,
  onCapture,
  capturedDataUrl,
  onClear,
}: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraOn(false);
  }, []);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  async function startCamera() {
    setCameraError(null);
    onClear();
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("이 브라우저는 카메라를 지원하지 않습니다");
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: "user",
          width: { ideal: 720 },
          height: { ideal: 960 },
        },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        await video.play();
      }
      setCameraOn(true);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "카메라 권한을 허용해 주세요";
      setCameraError(message);
      stopCamera();
    }
  }

  function snap() {
    const video = videoRef.current;
    if (!video || !cameraOn) return;

    const width = video.videoWidth || 720;
    const height = video.videoHeight || 960;
    const canvas = document.createElement("canvas");
    // downscale: demographics analysis needs no more than this, keeps vision token cost low
    const maxSide = 640;
    const scale = Math.min(1, maxSide / Math.max(width, height));
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setCameraError("캔버스를 초기화할 수 없습니다");
      return;
    }

    // mirror selfie like phone front camera
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
    const parts = dataUrlToParts(dataUrl);
    onCapture({
      dataUrl,
      base64: parts.base64,
      mimeType: parts.mimeType,
    });
    stopCamera();
  }

  return (
    <div className="camera-block">
      <div className="camera-frame">
        {capturedDataUrl ? (
          <img src={capturedDataUrl} alt="촬영한 셀카" className="camera-preview" />
        ) : (
          <video
            ref={videoRef}
            className={cameraOn ? "camera-preview mirrored" : "camera-preview hidden-video"}
            playsInline
            muted
            autoPlay
            aria-label={cameraOn ? "카메라 미리보기" : undefined}
          />
        )}

        {!cameraOn && !capturedDataUrl && (
          <button
            type="button"
            className="camera-placeholder"
            onClick={() => void startCamera()}
            disabled={disabled}
          >
            <span className="camera-placeholder-icon" aria-hidden>
              <IconCamera size={34} />
            </span>
            <span>탭해서 셀카 찍기</span>
          </button>
        )}

        {cameraOn && (
          <div className="shutter-bar">
            <button
              type="button"
              className="shutter-btn"
              onClick={snap}
              disabled={disabled}
              aria-label="촬영"
            />
            <button
              type="button"
              className="shutter-cancel"
              onClick={stopCamera}
              disabled={disabled}
            >
              취소
            </button>
          </div>
        )}

        {capturedDataUrl && (
          <button
            type="button"
            className="retake-btn"
            onClick={() => {
              onClear();
              void startCamera();
            }}
            disabled={disabled}
          >
            <IconRefresh size={16} />
            <span>다시 찍기</span>
          </button>
        )}
      </div>

      {cameraError && (
        <p className="camera-error" role="alert">
          {cameraError}
        </p>
      )}
    </div>
  );
}
